import { isEqual } from "lodash";

export type WarehouseData = Record<string, any>;

const warehouse: Record<string, WarehouseData> = {};

/**
 * Get warehouse data from the warehouse with the specified key.
 * @param key - identifier used to lookup warehouse data.
 * @param defaultValue - value to return if no warehouse data is set.
 * @returns WarehouseData for the key or the default value set is warehouse data is undefined.
 */
export function get(key: string, defaultValue: WarehouseData): WarehouseData {
  const warehouseValue = warehouse[key];
  return warehouseValue === undefined ? defaultValue : warehouseValue;
}

/**
 * Set the warehouse data for the specified warehouse key.
 * @param key - Identifier for where to store the specified warehouse data.
 * @param value -WarehouseData value to set in the given key.
 */
export function set(key: string, value: WarehouseData): void {
  warehouse[key] = value;
}

/**
 * Update the warehouse data for the specified warehouse key by appending the value to the end.
 * @param key - Identifier for where to store the specified warehouse data.
 * @param value -WarehouseData value to update in the given key.
 */
export function update(key: string, value: WarehouseData): void {
  warehouse[key] = Object.assign(
    {},
    ...Object.entries(value)
      .filter((x) => x[0] != "__typename")
      .map(([_key, value], i) => {
        return {
          [_key]: (Object.values({ ...warehouse[key] })[i] || []).concat(
            [...value] || []
          ),
        };
      })
  );
}

/**
 * Remove the warehouse key when the data is no longer needed.
 * @param key - Identifier of the warehouse item to be removed.
 * @returns WarehouseData removed from the warehouse or undefined if there was
 *   no warehouse data to delete.
 */
export function remove(key: string): WarehouseData | undefined {
  const removed = warehouse[key];
  if (removed !== undefined) {
    delete warehouse[key];
  }
  return removed;
}

/**
 * Clear the warehouse completely.
 */
export function clear(): void {
  Object.keys(warehouse).forEach((key) => {
    delete warehouse[key];
  });
}

/**
 * Gets the count of unique items in the warehouse.
 * It requires that the warehouse contains an 'id' key insdie.
 * @param key - Identifier of the warehouse item.
 * @returns - Number of unique rows in the warehouse item
 */
export function count(key: string): number {
  const data = get(key, {}) as WarehouseData;
  if (isEqual({}, data)) return 0;

  const count = data["id"].length;
  return count;
}
